// Map links for the property's location block.
//
// Two separate URLs, because they answer different questions: the embed is what
// the page draws inline, the link is what opens the seller's own maps app. Ops
// may have pinned a specific entrance in `googleMapsUrl` that the coordinates
// alone would miss, so that link wins over the generated one when it exists.
import type { PropertyDetail } from '../types/propertyTypes'

const MAP_ZOOM = 15

export interface PropertyMapLinks {
  /** Inline map, or null when the property has no coordinates. */
  embedUrl: string | null
  /** "Open in Maps" destination, or null when there is nothing to open. */
  mapsUrl: string | null
}

function toCoordinates(property: PropertyDetail): string | null {
  const { latitude, longitude } = property
  if (!latitude || !longitude) return null
  return `${latitude},${longitude}`
}

export function buildPropertyMapLinks(property: PropertyDetail): PropertyMapLinks {
  const coordinates = toCoordinates(property)
  const searchUrl = coordinates
    ? `https://www.google.com/maps/search/?api=1&query=${coordinates}`
    : null
  return {
    embedUrl: coordinates
      ? `https://maps.google.com/maps?q=${coordinates}&z=${MAP_ZOOM}&output=embed`
      : null,
    mapsUrl: property.googleMapsUrl || searchUrl,
  }
}

/**
 * The postal address as a person would write it, one line per line. Empty parts
 * are dropped rather than leaving blank rows in the block.
 */
export function buildPropertyAddressLines(property: PropertyDetail): string[] {
  const cityLine = [property.city, property.stateProvince, property.postalCode]
    .filter(Boolean)
    .join(', ')
  return [property.addressLine1, property.addressLine2, property.areaName, cityLine].filter(
    (line): line is string => Boolean(line),
  )
}
